import React, { useState, useEffect, useCallback } from 'react';
import { anomalyApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Warning, Check, WarningCircle, Trash } from '@phosphor-icons/react';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnomaliesPage = () => {
  const { user, isManager } = useAuth();
  const [anomalies, setAnomalies] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formData, setFormData] = useState({ target_user_id: '', date: '', expected_status: 'office', actual_status: 'absent', notes: '' });
  const [resolveData, setResolveData] = useState({ action: 'unpaid', hours: 8, notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [anomaliesRes, membersRes] = await Promise.all([
        anomalyApi.getAll(),
        axios.get(`${API_URL}/api/team-members`, { withCredentials: true }),
      ]);
      setAnomalies(anomaliesRes.data);
      setTeamMembers(membersRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await anomalyApi.create(formData);
      toast.success('Segnalazione inviata');
      setIsCreateOpen(false);
      setFormData({ target_user_id: '', date: '', expected_status: 'office', actual_status: 'absent', notes: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const handleResolve = async () => {
    if (!selectedAnomaly) return;
    setSubmitting(true);
    try {
      await anomalyApi.resolve(selectedAnomaly.id, resolveData);
      toast.success('Segnalazione risolta');
      setIsResolveOpen(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const toggleSelect = (id) => { const n = new Set(selectedIds); n.has(id) ? n.delete(id) : n.add(id); setSelectedIds(n); };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} segnalazioni?`)) return;
    try {
      await anomalyApi.bulkDelete([...selectedIds]);
      toast.success(`${selectedIds.size} segnalazioni eliminate`);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAnomalies = anomalies.filter(a => a.status === 'open');
  const resolvedAnomalies = anomalies.filter(a => a.status === 'resolved');

  const StatusLabels = { office: 'In Ufficio', smart_working: 'Smart Working', absent: 'Assente', on_call: 'Reperibilità', leave: 'Ferie' };

  const renderTable = (items, showResolve = false) => (
    <div className="border border-[#E4E4E7] overflow-x-auto">
      <table className="w-full data-table min-w-[700px]">
        <thead>
          <tr className="border-b-2 border-[#0A0A0A] bg-white">
            <th className="py-4 px-3 text-left w-10">
              <Checkbox checked={items.length > 0 && items.every(a => selectedIds.has(a.id))}
                onCheckedChange={() => { const all = items.every(a => selectedIds.has(a.id)); const n = new Set(selectedIds); items.forEach(a => all ? n.delete(a.id) : n.add(a.id)); setSelectedIds(n); }} />
            </th>
            <th className="py-4 px-4 text-left">Dipendente</th>
            <th className="py-4 px-4 text-left">Data</th>
            <th className="py-4 px-4 text-left">Stato Previsto</th>
            <th className="py-4 px-4 text-left">Stato Effettivo</th>
            <th className="py-4 px-4 text-left">Segnalato da</th>
            <th className="py-4 px-4 text-left">Note</th>
            {showResolve && <th className="py-4 px-4 text-right">Azioni</th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={showResolve ? 8 : 7} className="py-12 text-center text-[#52525B]">Nessuna segnalazione</td></tr>
          ) : items.map(a => (
            <tr key={a.id} className={`border-b border-[#E4E4E7] hover:bg-[#F4F4F5] ${selectedIds.has(a.id) ? 'bg-[#FF2A00]/5' : ''}`} data-testid={`anomaly-${a.id}`}>
              <td className="py-3 px-3"><Checkbox checked={selectedIds.has(a.id)} onCheckedChange={() => toggleSelect(a.id)} /></td>
              <td className="py-3 px-4 font-medium">{a.target_user_name}</td>
              <td className="py-3 px-4 font-mono text-sm">{a.date}</td>
              <td className="py-3 px-4 text-sm">{StatusLabels[a.expected_status] || a.expected_status}</td>
              <td className="py-3 px-4">
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border border-[#FF2A00] text-[#FF2A00] bg-[#FF2A00]/5">
                  {StatusLabels[a.actual_status] || a.actual_status}
                </span>
              </td>
              <td className="py-3 px-4 text-sm">{a.reporter_name}</td>
              <td className="py-3 px-4 text-sm text-[#52525B] max-w-xs truncate">{a.notes || '-'}</td>
              {showResolve && isManager && (
                <td className="py-3 px-4 text-right">
                  <button onClick={() => { setSelectedAnomaly(a); setResolveData({ action: 'unpaid', hours: 8, notes: '' }); setIsResolveOpen(true); }}
                    className="w-8 h-8 flex items-center justify-center border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                    data-testid={`resolve-${a.id}`} title="Risolvi">
                    <Check size={16} weight="bold" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="anomalies-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Segnalazioni Difformità</h1>
          <p className="text-[#52525B] mt-1">Segnala e gestisci difformità nella presenza dei dipendenti</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <Button onClick={handleBulkDelete}
              className="bg-[#FF2A00] text-white px-5 py-2.5 font-medium hover:bg-[#D92400] rounded-none" data-testid="bulk-delete-anomalies-btn">
              <Trash size={18} weight="bold" className="mr-2" /> Elimina ({selectedIds.size})
            </Button>
          )}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF2A00] text-white px-6 py-2.5 font-medium hover:bg-[#D92400] rounded-none" data-testid="new-anomaly-btn">
              <Plus size={20} weight="bold" className="mr-2" />
              Nuova Segnalazione
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Segnala Difformità</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Dipendente</Label>
                <Select value={formData.target_user_id} onValueChange={v => setFormData({...formData, target_user_id: v})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="anomaly-user-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {teamMembers.map(m => <SelectItem key={m.id} value={m.id} className="rounded-none">{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data</Label>
                <Input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="rounded-none border-[#E4E4E7]" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Stato Previsto</Label>
                  <Select value={formData.expected_status} onValueChange={v => setFormData({...formData, expected_status: v})}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="office" className="rounded-none">In Ufficio</SelectItem>
                      <SelectItem value="smart_working" className="rounded-none">Smart Working</SelectItem>
                      <SelectItem value="on_call" className="rounded-none">Reperibilità</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Stato Effettivo</Label>
                  <Select value={formData.actual_status} onValueChange={v => setFormData({...formData, actual_status: v})}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="absent" className="rounded-none">Assente</SelectItem>
                      <SelectItem value="smart_working" className="rounded-none">Smart Working</SelectItem>
                      <SelectItem value="leave" className="rounded-none">Ferie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Note</Label>
                <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="rounded-none border-[#E4E4E7] min-h-20" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-[#FF2A00] text-white hover:bg-[#D92400] rounded-none" data-testid="submit-anomaly-btn">
                  {submitting ? 'Invio...' : 'Segnala'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <Tabs defaultValue="open" className="space-y-6">
          <TabsList className="border border-[#E4E4E7] bg-white p-1 rounded-none">
            <TabsTrigger value="open" className="rounded-none data-[state=active]:bg-[#FF2A00] data-[state=active]:text-white px-6">Aperte ({openAnomalies.length})</TabsTrigger>
            <TabsTrigger value="resolved" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-6">Risolte</TabsTrigger>
          </TabsList>
          <TabsContent value="open">{renderTable(openAnomalies, true)}</TabsContent>
          <TabsContent value="resolved">{renderTable(resolvedAnomalies, false)}</TabsContent>
        </Tabs>
      )}

      <Dialog open={isResolveOpen} onOpenChange={setIsResolveOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Risolvi Segnalazione</DialogTitle></DialogHeader>
          {selectedAnomaly && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4">
                <p className="text-sm"><b>{selectedAnomaly.target_user_name}</b> - {selectedAnomaly.date}</p>
                <p className="text-sm text-[#52525B]">Previsto: {StatusLabels[selectedAnomaly.expected_status]} | Effettivo: {StatusLabels[selectedAnomaly.actual_status]}</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Azione</Label>
                <Select value={resolveData.action} onValueChange={v => setResolveData({...resolveData, action: v})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="leave" className="rounded-none">Scalare da Ferie</SelectItem>
                    <SelectItem value="rol" className="rounded-none">Scalare da ROL</SelectItem>
                    <SelectItem value="unpaid" className="rounded-none">Permesso Non Retribuito</SelectItem>
                    <SelectItem value="none" className="rounded-none">Nessuna Azione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Ore da Scalare</Label>
                <Input type="number" step="0.5" min="0.5" max="24" value={resolveData.hours}
                  onChange={e => setResolveData({...resolveData, hours: parseFloat(e.target.value) || 0})}
                  className="rounded-none border-[#E4E4E7] w-32" data-testid="anomaly-hours-input" />
                <p className="text-xs text-[#52525B] mt-1">1 giornata lavorativa = 8 ore</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Note</Label>
                <Textarea value={resolveData.notes} onChange={e => setResolveData({...resolveData, notes: e.target.value})} className="rounded-none border-[#E4E4E7] min-h-20" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsResolveOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button type="button" disabled={submitting} onClick={handleResolve} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none">
                  {submitting ? 'Salvataggio...' : 'Risolvi'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnomaliesPage;
